/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import * as React from 'react';
import { theme as ds_theme } from '@web-stories-wp/design-system';
import {
  forwardRef,
  render,
  unmountComponentAtNode,
} from '@web-stories-wp/react';
import {
  PAGE_RATIO,
  FULLBLEED_RATIO,
  UnitsProvider,
} from '@web-stories-wp/units';
import styled, { ThemeProvider } from 'styled-components';
import { generatePatternStyles, type Pattern } from '@web-stories-wp/patterns';
import { TransformProvider } from '../../../components/transform';
import type { Page } from '@src/web-stories-wp/story-editor/src/elements/types';

/**
 * Internal dependencies
 */
import { FontProvider } from '../../font';
import APIContext from '../../api/context';
import FileContext from '../../file/context';
import StoryContext from '../../story/context';
import ConfigContext from '../../config/context';
import LutFilterContext from '../../lutFilters/context';
import DisplayElement from '../../../components/canvas/displayElement';

const StoryPage = styled.div<{ height: number; width: number }>`
  display: block;
  position: relative;
  padding: 0;
  border: 0;
  background-color: transparent;
  height: ${({ height }) => height}px;
  width: ${({ width }) => width}px;
  flex: none;
  outline: 0;
`;

const PreviewWrapper = styled.div<{ background: Pattern }>`
  height: 100%;
  position: relative;
  overflow: hidden;
  background-color: ${({ theme }) =>
    (theme as typeof ds_theme).colors.standard.white};
  border-radius: 4px;
  ${({ background }) =>
    generatePatternStyles(background, 'background', null, {
      width: FULLBLEED_RATIO,
      height: 1,
    })}
`;

const FullHeight = styled.div<{ yOffset: number }>`
  position: absolute;
  top: ${({ yOffset }) => yOffset}px;
  bottom: ${({ yOffset }) => yOffset}px;
  right: 0;
  left: 0;
`;

interface PageWithDepsProps {
  page: Page;
  width: number;
  height: number;
  renderFullHeightThumb?: boolean;
  containerHeight: number;
  contexts?: {
    api?: unknown;
    file?: unknown;
    story?: unknown;
    config?: unknown;
    lut?: unknown;
  };
}

const noop = () => undefined;

const defaultApiContext = { state: {}, actions: { getFonts: async () => [] } };
const defaultFileContext = {
  state: {},
  actions: {
    getFonts: async () => [],
  },
};
const defaultStoryContext = {
  state: {},
  actions: { updateElementsById: noop },
};
const defaultConfigContext = {};
const defaultLutFilterContext = { state: {}, actions: {} };

const PageWithDependencies = forwardRef<HTMLDivElement, PageWithDepsProps>(
  function PageWithDependencies(
    {
      page,
      width,
      height,
      renderFullHeightThumb = false,
      containerHeight,
      contexts,
    }: PageWithDepsProps,
    ref
  ) {
    return (
      <ThemeProvider theme={ds_theme}>
        <ConfigContext.Provider
          value={contexts?.config ?? defaultConfigContext}
        >
          <APIContext.Provider value={contexts?.api ?? defaultApiContext}>
            <FileContext.Provider value={contexts?.file ?? defaultFileContext}>
              <StoryContext.Provider
                value={contexts?.story ?? defaultStoryContext}
              >
                <LutFilterContext.Provider
                  value={contexts?.lut ?? defaultLutFilterContext}
                >
                  <FontProvider>
                    <TransformProvider>
                      <UnitsProvider
                        pageSize={{
                          width,
                          height,
                        }}
                      >
                        <StoryPage
                          ref={ref}
                          height={
                            renderFullHeightThumb ? containerHeight : height
                          }
                          width={width}
                        >
                          <PreviewWrapper background={page.backgroundColor}>
                            <FullHeight
                              yOffset={
                                renderFullHeightThumb
                                  ? (containerHeight - height) / 2
                                  : 0
                              }
                            >
                              {page.elements.map((element) => (
                                <DisplayElement
                                  key={element.id}
                                  previewMode
                                  previewPaddingRatio={
                                    renderFullHeightThumb
                                      ? Math.max(
                                          0,
                                          (containerHeight - height) /
                                            (2 * height)
                                        )
                                      : 0
                                  }
                                  element={element}
                                />
                              ))}
                            </FullHeight>
                          </PreviewWrapper>
                        </StoryPage>
                      </UnitsProvider>
                    </TransformProvider>
                  </FontProvider>
                </LutFilterContext.Provider>
              </StoryContext.Provider>
            </FileContext.Provider>
          </APIContext.Provider>
        </ConfigContext.Provider>
      </ThemeProvider>
    );
  }
);

/**
 * @typedef {import('@web-stories-wp/story-editor/src/elements/types').Page} Page
 */

interface Options {
  renderFullHeightThumb?: boolean;
  contexts?: PageWithDepsProps['contexts'];
}
/**
 * Takes a story page and generates a DOM node containing the rendered
 * page. Returns a tuple containing the page DOM node and a cleanup
 * method to remove the returned DOM node.
 *
 * **IMPORTANT:** Not calling the returned `cleanup()` method after use of
 * page DOM node will result in memory leak.
 */
async function storyPageToNode(
  page: Page,
  width: number,
  opts: Options = {}
): Promise<[HTMLElement, () => void]> {
  const { renderFullHeightThumb = false, contexts } = opts;
  const height = width * (1 / PAGE_RATIO);
  const containerHeight = width * (1 / FULLBLEED_RATIO);

  const bufferRoot = document.createElement('div');
  bufferRoot.style.cssText = `
     contain: strict;
     position: absolute;
     top: 0;
     left: 0;
     width: ${width}px;
     height: ${containerHeight}px;
     opacity: 0;
     transform: translate(-100%, -100%);
     pointer-events: none;
   `;

  const node: HTMLElement = await new Promise((resolve) => {
    const resolverRef = (htmlNode: HTMLDivElement) => {
      if (!htmlNode) {
        return;
      }
      resolve(htmlNode);
    };

    render(
      <PageWithDependencies
        ref={resolverRef}
        page={page}
        width={width}
        height={height}
        containerHeight={containerHeight}
        renderFullHeightThumb={renderFullHeightThumb}
        contexts={contexts}
      />,
      bufferRoot
    );
  });

  document.body.appendChild(bufferRoot);
  const cleanup = () => {
    unmountComponentAtNode(bufferRoot);
    document.body.removeChild(bufferRoot);
  };

  return [node, cleanup];
}
export default storyPageToNode;
